import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Category() {
  const { slug } = useParams();
  const [activeSlug, setActiveSlug] = useState(slug || "saishi-zixun");

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: currentCategory?.name || "分类",
    description: currentCategory?.description || "",
    url: `${window.location.origin}/category/${activeSlug}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: filteredArticles.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${window.location.origin}/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: currentCategory?.name || "分类" },
              ]}
            />
            <div className="px-4 md:px-6 pb-6">
              <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-900 mb-2">
                {currentCategory?.name}
              </h1>
              <p className="text-foreground-500 text-sm">
                {currentCategory?.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-50 border-b border-background-200/60 overflow-x-auto">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-white"
                      : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2 flex-1">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-700 mb-2">暂无内容</h3>
                <p className="text-sm text-foreground-500">该分类下还没有文章，请稍后再来</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}